import { useId, useState, type HTMLAttributes, type KeyboardEvent } from 'react';
import { Icono } from './Icono';

/**
 * RV Campo (componentes.md §8): el único campo de texto del panel y de las hojas rojas. Píldora `--rv-paper` de 58 px,
 * etiqueta flotante, foco y error = anillo interior de 2 px rojo. Error: una línea roja con icono (y una sacudida de 4 px);
 * ayuda: una línea gris. Contraseña: botón ojo de 58 pegado. Cada tipo pone su `autocomplete`, `inputmode` y
 * `autocapitalize`. Fuente siempre 16 px (Safari no hace zoom). Con `filas`, un texto de varias líneas (RV Campo
 * `tipo="textarea"` del diseño): esquinas de 22 y la etiqueta siempre arriba. `tema="oscuro"` para el widget oscuro (G618).
 */
export type TipoCampo = 'text' | 'email' | 'password' | 'tel' | 'number' | 'url' | 'search';

interface Props {
    etiqueta: string;
    valor: string;
    onChange: (valor: string) => void;
    tipo?: TipoCampo;
    autocompletar?: string;
    modo?: HTMLAttributes<HTMLInputElement>['inputMode'];
    mayusculas?: string;
    error?: string | null;
    ayuda?: string | null;
    prefijo?: string;
    bloqueado?: boolean;
    max?: number;
    nombre?: string;
    id?: string;
    autoFocus?: boolean;
    onEnter?: () => void;
    /** Al salir del campo (perder el foco): para confirmar lo escrito, como las medidas del plano (G1208). */
    onSalir?: () => void;
    /** Texto de varias líneas con este alto inicial (sin Enter para enviar). */
    filas?: number;
    /** `oscuro` en el widget oscuro (G618); sin la prop, como siempre. */
    tema?: 'claro' | 'oscuro';
}

const MODO: Partial<Record<TipoCampo, HTMLAttributes<HTMLInputElement>['inputMode']>> = { email: 'email', tel: 'tel', number: 'decimal', url: 'url', search: 'search' };
const AUTO: Partial<Record<TipoCampo, string>> = { email: 'email', password: 'current-password', tel: 'tel' };

export function Campo({ etiqueta, valor, onChange, tipo = 'text', autocompletar, modo, mayusculas, error, ayuda, prefijo, bloqueado = false, max, nombre, id, autoFocus, onEnter, onSalir, filas, tema = 'claro' }: Props) {
    const propio = useId();
    const idCampo = id ?? propio;
    const [foco, setFoco] = useState(false);
    const [visible, setVisible] = useState(false);
    const esClave = tipo === 'password';
    const hayError = error != null && error !== '';
    const hayAyuda = !hayError && ayuda != null && ayuda !== '';
    const area = filas !== undefined;
    const arriba = area || foco || valor !== '';
    const idNota = `${idCampo}-nota`;

    const tecla = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && onEnter !== undefined) {
            e.preventDefault();
            onEnter();
        }
    };

    const caja = ['rv-campo__caja', area ? 'rv-campo__caja--area' : '', esClave ? 'rv-campo__caja--clave' : '', bloqueado ? 'rv-campo__caja--bloqueado' : '', foco ? 'rv-campo__caja--foco' : '', hayError ? 'rv-campo__caja--error' : '']
        .filter((c) => c !== '')
        .join(' ');

    return (
        <div className={`rv-campo${tema === 'oscuro' ? ' rv-tema-oscuro' : ''}`} data-rv-tema={tema}>
            <div className={`rv-campo__fila${hayError ? ' rv-campo__fila--sacude' : ''}`}>
                <label htmlFor={idCampo} className={caja}>
                    <span className={`rv-campo__etiqueta${arriba ? ' rv-campo__etiqueta--arriba' : ''}`}>{etiqueta}</span>
                    {prefijo !== undefined && <span className="rv-campo__prefijo" style={{ opacity: arriba ? 1 : 0 }}>{prefijo}</span>}
                    {area ? (
                        <textarea
                            id={idCampo}
                            name={nombre ?? idCampo}
                            rows={filas}
                            value={valor}
                            disabled={bloqueado}
                            maxLength={max}
                            aria-invalid={hayError}
                            aria-describedby={hayError || hayAyuda ? idNota : undefined}
                            onChange={(e) => {
                                onChange(e.target.value);
                            }}
                            onFocus={() => {
                                setFoco(true);
                            }}
                            onBlur={() => {
                                setFoco(false);
                            }}
                        />
                    ) : (
                        <input
                            id={idCampo}
                            name={nombre ?? idCampo}
                            type={esClave ? (visible ? 'text' : 'password') : tipo === 'number' ? 'text' : tipo}
                            value={valor}
                            disabled={bloqueado}
                            maxLength={max}
                            autoFocus={autoFocus}
                            autoComplete={autocompletar ?? AUTO[tipo] ?? 'off'}
                            inputMode={modo ?? MODO[tipo] ?? 'text'}
                            autoCapitalize={mayusculas ?? (['email', 'password', 'url'].includes(tipo) ? 'none' : 'sentences')}
                            spellCheck={!['email', 'password', 'url', 'tel', 'number'].includes(tipo)}
                            aria-invalid={hayError}
                            aria-describedby={hayError || hayAyuda ? idNota : undefined}
                            onChange={(e) => {
                                onChange(e.target.value);
                            }}
                            onFocus={() => {
                                setFoco(true);
                            }}
                            onBlur={() => {
                                setFoco(false);
                                onSalir?.();
                            }}
                            onKeyDown={tecla}
                        />
                    )}
                </label>
                {esClave && (
                    <button
                        type="button"
                        className="rv-campo__ojo"
                        aria-label={visible ? 'Ocultar la contraseña' : 'Mostrar la contraseña'}
                        aria-pressed={visible}
                        style={{ boxShadow: foco || hayError ? 'inset 0 0 0 2px var(--rv-red)' : undefined }}
                        onClick={() => {
                            setVisible((v) => !v);
                        }}
                    >
                        <Icono nombre={visible ? 'eye-off' : 'eye'} />
                    </button>
                )}
            </div>
            {hayError && (
                <span id={idNota} role="alert" className="rv-campo__error">
                    <Icono nombre="circle-alert" />
                    <span>{error}</span>
                </span>
            )}
            {hayAyuda && (
                <span id={idNota} className="rv-campo__ayuda">
                    {ayuda}
                </span>
            )}
        </div>
    );
}
